import React, { useEffect, useMemo, useState } from 'react';
import { Button, FormControl, TextField } from '@mui/material';
import toaster from 'react-hot-toast';
import AutocompleteDropdown from 'components/common/autocompleteDropdown/AutocompleteDropdown';
import { BusinessPicker } from 'components/common/businessPicker/BusinessPicker';
import { EnvironmentalVariables } from 'components/common/environmentalVariables/EnvironmentalVariables';
import { ReleaseVersionSelect } from 'components/common/firmware/ReleaseVersionSelect';
import type { ReleaseVersion } from 'api/admin/firmware/firmware';
import {
  type WashboardDeviceDetails,
  updateWashboardDevice,
  type UpdateWashboardDevicePayload,
  isReleaseUpdateActive,
} from 'api/admin/washboard/washboard';
import { getEnvVarDiff, toEnvVarChangeLists } from '../utils/envVarDiff';
import { ConfirmChangesModal } from './ConfirmChangesModal';
import './DeviceConfiguration.scss';

type Option = { value: string | number; display: string };

type DeviceConfigurationProps = {
  deviceDetails: WashboardDeviceDetails;
  business: { id: number | null };
  location: { id: number | null; options: Option[]; businessId: number | null };
  setBusinessId: (id: number | null) => void;
  setStoreId: (id: number | null) => void;
  deviceId: number;
  onUpdateSuccess?: () => void;
};

const getRelease = (deviceDetails?: WashboardDeviceDetails | null): ReleaseVersion | null =>
  deviceDetails?.releaseId
    ? { id: deviceDetails.releaseId, version: deviceDetails.releaseVersion ?? '' }
    : null;

const getEnvVariables = (deviceDetails?: WashboardDeviceDetails | null) => {
  const pending = deviceDetails?.pendingEnvVariables;
  if (pending && Object.keys(pending).length > 0) {
    return pending;
  }
  return deviceDetails?.environmentVariables || {};
};

const DeviceConfiguration: React.FC<DeviceConfigurationProps> = ({
  deviceDetails,
  business,
  location,
  setBusinessId,
  setStoreId,
  deviceId,
  onUpdateSuccess,
}) => {
  const [machineName, setMachineName] = useState(deviceDetails?.machineName ?? '');
  const [upperMachineName, setUpperMachineName] = useState(deviceDetails?.machineUpperName ?? '');
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | null>(business.id);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(location.id);
  const [selectedRelease, setSelectedRelease] = useState<ReleaseVersion | null>(
    getRelease(deviceDetails),
  );
  const [envVariables, setEnvVariables] = useState<Record<string, string>>(
    getEnvVariables(deviceDetails),
  );
  const [hasEnvVarErrors, setHasEnvVarErrors] = useState(false);
  const [hasEnvChanges, setHasEnvChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  useEffect(() => {
    setMachineName(deviceDetails?.machineName ?? '');
    setUpperMachineName(deviceDetails?.machineUpperName ?? '');
    setSelectedRelease(getRelease(deviceDetails));
    setEnvVariables(getEnvVariables(deviceDetails));
  }, [deviceDetails]);

  useEffect(() => {
    setSelectedBusinessId(business.id);
  }, [business.id]);

  useEffect(() => {
    setSelectedLocationId(location.id);
  }, [location.id]);

  const selectedStoreOption = useMemo(() => {
    const option = location.options.find((s) => s.value === selectedLocationId) ?? null;
    return {
      option,
      key: `store-${selectedBusinessId}-${option?.value}-${location.options.length}`,
    };
  }, [location.options, selectedLocationId, selectedBusinessId]);

  const isDevicePaired = useMemo(() => !!deviceDetails?.machineName, [deviceDetails?.machineName]);

  const hasActiveReleaseUpdate = useMemo(
    () => isReleaseUpdateActive(deviceDetails?.releaseUpdate?.status),
    [deviceDetails?.releaseUpdate?.status],
  );

  const hasReleaseChanged = useMemo(
    () => Boolean(selectedRelease) && selectedRelease?.id !== (deviceDetails?.releaseId ?? null),
    [selectedRelease, deviceDetails?.releaseId],
  );

  const originalEnvVars = useMemo(() => getEnvVariables(deviceDetails), [deviceDetails]);
  const envVarDiff = useMemo(
    () => getEnvVarDiff(originalEnvVars, envVariables),
    [originalEnvVars, envVariables],
  );

  const hasChanges = useMemo(() => {
    const machineNameChanged = machineName !== (deviceDetails?.machineName ?? '');
    const upperMachineNameChanged = upperMachineName !== (deviceDetails?.machineUpperName ?? '');
    const businessChanged =
      !isDevicePaired && selectedBusinessId !== (deviceDetails?.businessId ?? null);
    const locationChanged =
      !isDevicePaired && selectedLocationId !== (deviceDetails?.locationId ?? null);

    const envVarsChanged = envVarDiff.hasChanges || hasEnvChanges;

    return (
      machineNameChanged ||
      upperMachineNameChanged ||
      businessChanged ||
      locationChanged ||
      envVarsChanged ||
      hasReleaseChanged
    );
  }, [
    machineName,
    upperMachineName,
    selectedBusinessId,
    selectedLocationId,
    envVariables,
    envVarDiff.hasChanges,
    hasEnvChanges,
    hasReleaseChanged,
    deviceDetails?.machineName,
    deviceDetails?.machineUpperName,
    deviceDetails?.businessId,
    deviceDetails?.locationId,
    isDevicePaired,
  ]);

  const isSaveDisabled = useMemo(
    () =>
      isSaving ||
      hasEnvVarErrors ||
      (!isDevicePaired && selectedLocationId === null) ||
      (isDevicePaired && machineName.trim() === ''),
    [isSaving, hasEnvVarErrors, isDevicePaired, selectedLocationId, machineName],
  );

  const changesList = useMemo(() => {
    const changes: {
      machineName?: string;
      machineUpperName?: string;
      location?: string;
      releaseVersion?: string;
      addedEnvVars?: string[];
      modifiedEnvVars?: string[];
      deletedEnvVars?: string[];
    } = {};

    if (machineName !== (deviceDetails?.machineName ?? '')) {
      changes.machineName = machineName;
    }

    if (
      deviceDetails?.machineUpperName &&
      upperMachineName !== (deviceDetails?.machineUpperName ?? '')
    ) {
      changes.machineUpperName = upperMachineName;
    }

    if (
      !isDevicePaired &&
      selectedLocationId &&
      selectedLocationId !== (deviceDetails?.locationId ?? null)
    ) {
      const locationName = location.options.find(
        (opt) => opt.value === selectedLocationId,
      )?.display;
      changes.location = locationName || `ID: ${selectedLocationId}`;
    }

    if (hasReleaseChanged && selectedRelease) {
      changes.releaseVersion = selectedRelease.version || `ID: ${selectedRelease.id}`;
    }

    if (envVarDiff.hasChanges) {
      const envVarChanges = toEnvVarChangeLists(envVariables, envVarDiff);
      changes.addedEnvVars = envVarChanges.addedEnvVars;
      changes.modifiedEnvVars = envVarChanges.modifiedEnvVars;
      changes.deletedEnvVars = envVarChanges.deletedEnvVars;
    }

    return changes;
  }, [
    machineName,
    upperMachineName,
    selectedLocationId,
    envVariables,
    envVarDiff,
    deviceDetails,
    location.options,
    isDevicePaired,
    hasReleaseChanged,
    selectedRelease,
  ]);

  const handleCancel = () => {
    setMachineName(deviceDetails?.machineName ?? '');
    setUpperMachineName(deviceDetails?.machineUpperName ?? '');
    if (!isDevicePaired) {
      const originalBusinessId = deviceDetails?.businessId ?? null;
      const originalLocationId = deviceDetails?.locationId ?? null;

      setSelectedBusinessId(originalBusinessId);
      setBusinessId(originalBusinessId);
      setSelectedLocationId(originalLocationId);
      setStoreId(originalLocationId);
    }
    setSelectedRelease(getRelease(deviceDetails));
    setEnvVariables(getEnvVariables(deviceDetails));
  };

  const handleEnvVariablesChange = (
    newEnvVars: Record<string, string>,
    hasErrors: boolean,
    hasChanges: boolean,
  ) => {
    setEnvVariables(newEnvVars);
    setHasEnvVarErrors(hasErrors);
    setHasEnvChanges(hasChanges);
  };

  const handleSave = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const payload: UpdateWashboardDevicePayload = {};

      if (machineName !== (deviceDetails?.machineName ?? '')) {
        payload.machineName = machineName;
      }

      if (
        deviceDetails?.machineUpperName &&
        upperMachineName !== (deviceDetails?.machineUpperName ?? '')
      ) {
        payload.upperMachineName = upperMachineName;
      }

      if (
        !isDevicePaired &&
        selectedLocationId &&
        selectedLocationId !== (deviceDetails?.locationId ?? null)
      ) {
        payload.locationId = selectedLocationId;
      }

      if (hasReleaseChanged && selectedRelease) {
        payload.releaseId = selectedRelease.id;
      }

      if (envVarDiff.hasChanges) {
        payload.envVariables = envVariables;
      }

      await updateWashboardDevice(deviceId, payload);
      toaster.success('Update process was started for the device.');
      setShowConfirmModal(false);
      setSelectedRelease(getRelease(deviceDetails));
      onUpdateSuccess?.();
    } catch (error: any) {
      toaster.error(error?.response?.data?.error || 'Failed to update device configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="device-configuration">
      <div className="configuration-tab-content">
        <div className="configuration-section">
          <div className="configuration-row">
            <div className="configuration-field">
              <TextField
                label="Machine Name"
                value={machineName}
                onChange={(e) => setMachineName(e.target.value)}
                fullWidth
                size="small"
                disabled={!isDevicePaired}
              />
            </div>
            {deviceDetails?.machineUpperName && (
              <div className="configuration-field">
                <TextField
                  label="Machine Upper Name"
                  value={upperMachineName}
                  onChange={(e) => setUpperMachineName(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={!isDevicePaired}
                />
              </div>
            )}
            <div className="configuration-field">
              <TextField
                label="Communication Protocol"
                value={deviceDetails?.communicationProtocol ?? 'N/A'}
                fullWidth
                size="small"
                disabled
              />
            </div>
            <div className="configuration-field">
              <FormControl fullWidth>
                <BusinessPicker
                  id="device-business"
                  selectedBusinessId={selectedBusinessId}
                  onChange={(id) => {
                    setSelectedBusinessId(id);
                    setBusinessId(id);
                    setStoreId(null);
                    setSelectedLocationId(null);
                  }}
                  size="small"
                  disabled={isDevicePaired}
                />
              </FormControl>
            </div>
            <div className="configuration-field">
              <FormControl fullWidth>
                <AutocompleteDropdown
                  id="device-store"
                  key={selectedStoreOption.key}
                  selectedData={selectedStoreOption.option}
                  onChange={(option) => {
                    const value = option?.value ?? null;
                    setSelectedLocationId(value as number | null);
                  }}
                  label="Location"
                  selectData={location.options}
                  size="small"
                  disabled={isDevicePaired}
                />
              </FormControl>
            </div>
            {deviceDetails?.deviceType && (
              <div className="configuration-field">
                <FormControl fullWidth>
                  <ReleaseVersionSelect
                    id="device-release"
                    deviceType={deviceDetails.deviceType}
                    selectedRelease={selectedRelease}
                    onChange={setSelectedRelease}
                    size="small"
                    disabled={hasActiveReleaseUpdate}
                  />
                </FormControl>
              </div>
            )}
          </div>
        </div>

        <div className="env-vars-section">
          <EnvironmentalVariables
            envVariables={envVariables}
            onChange={handleEnvVariablesChange}
            isPending={Object.keys(deviceDetails?.pendingEnvVariables || {}).length > 0}
          />
        </div>

        {hasChanges && (
          <>
            <div className="configuration-actions">
              <Button variant="outlined" color="primary" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isSaveDisabled}
              >
                Save Changes
              </Button>
            </div>
          </>
        )}
      </div>

      <ConfirmChangesModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
        deviceName={deviceDetails?.deviceName || ''}
        changes={changesList}
        isLoading={isSaving}
      />
    </div>
  );
};

export default DeviceConfiguration;
